import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import { MainPanel } from './MainPanel';
import { Sidebar } from './Sidebar';
import OrganizationsApi from '../api/OrganizationApi';
import AuthorApi from '../api/AuthorApi';
import ClipApi from '../api/ClipApi';
import SummaryApi from '../api/SummaryApi';
import { context } from './context';
import { context as globalContext } from '../context';

const PAGE_SIZE = 20; // 每页显示数量

export const Home = () => {
    
    const [organizations, setOrganizations] = React.useState([]);
    const [authors, setAuthors] = React.useState([]);
    const [selectedAuthors, setSelectedAuthors] = React.useState([]);
    const [searchWord, setSearchWord] = React.useState('');
    const [clips, setClips] = React.useState([]);
    
    // --- 新增状态：管理直播形式和内容的筛选 ---
    const [selectedFormat, setSelectedFormat] = React.useState('');
    const [selectedContent, setSelectedContent] = React.useState('');
    // --- 新增状态：时间范围筛选 ---
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    // --- 新增状态：分页 ---
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalClips, setTotalClips] = React.useState(0);
    const totalPages = Math.ceil(totalClips / PAGE_SIZE);
    // --- 新增状态：搜索类型 ---
    const [searchType, setSearchType] = React.useState('subtitle'); // 'subtitle' | 'summary'

    const { setLoading, onMessage } = React.useContext(globalContext);
    const theme = useTheme();
    // eslint-disable-next-line no-unused-vars
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const params = useParams();

    // 切换社团时自动重置分页到第一页
    React.useEffect(() => {
        setCurrentPage(1);
    }, [params.organizationId]);

    // 搜索处理函数
    const onSearch = (inputText) => {
        setSearchWord(inputText);
        setCurrentPage(1); // 搜索时重置到第一页
    };

    // 页码变化处理
    const onPageChange = (event, page) => {
        setCurrentPage(page);
    };

    // 筛选条件变化时重置到第一页
    React.useEffect(() => {
        setCurrentPage(1);
    }, [selectedFormat, selectedContent, startDate, endDate, searchType]);

    // 获取社团列表
    React.useEffect(() => {
        (async () => {
            const json = await OrganizationsApi.findAll();
            setOrganizations(json || []);
        })();
    }, []);

    // --- 修改：重构核心数据获取逻辑 ---
    // 当组织、搜索词、直播形式或内容筛选变化时，重新获取数据
    React.useEffect(() => {
        (async () => {
            if (params.organizationId) {
                setLoading(true);
                try {
                    // 第一次进入组织页面时，获取作者列表并默认全选
                    if (authors.length === 0 || authors[0]?.organizationId !== parseInt(params.organizationId)) {
                        const authorsData = await AuthorApi.findByOrganizationId(params.organizationId);
                        const newAuthors = authorsData || [];
                        setAuthors(newAuthors);
                        setSelectedAuthors(newAuthors);
                    }
                    
                    // 根据搜索类型选择不同的搜索方式
                    if (searchType === 'summary' && searchWord) {
                        // 搜索 AI 总结
                        const result = await SummaryApi.searchByKeyword(
                            params.organizationId,
                            searchWord,
                            currentPage,
                            PAGE_SIZE
                        );
                        setClips(result.clips || []);
                        setTotalClips(result.total || 0);
                    } else {
                        // 根据所有筛选条件获取视频列表（带分页）
                        const result = await ClipApi.findByOrganizationId(
                            params.organizationId, 
                            searchWord, 
                            selectedFormat, 
                            selectedContent, 
                            startDate, 
                            endDate,
                            currentPage,
                            PAGE_SIZE
                        );
                        setClips(result.clips || []);
                        setTotalClips(result.total || 0);
                    }

                } catch (ex) {
                    console.log(ex);
                    onMessage({
                        type: 'error',
                        content: ex.message || '内部错误'
                    });
                } finally {
                    setLoading(false);
                }
            } else {
                // 如果没有选择组织，则清空所有数据
                setSearchWord('');
                setAuthors([]);
                setSelectedAuthors([]);
                setClips([]);
                setSelectedFormat('');
                setSelectedContent('');
                setStartDate('');
                setEndDate('');
                setCurrentPage(1);
                setTotalClips(0);
                setSearchType('subtitle');
            }
        })();
    // 依赖项数组包含了所有可能触发数据重新获取的变量
    // 注意：authors, setLoading, onMessage 故意不加入依赖，避免无限循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.organizationId, searchWord, selectedFormat, selectedContent, startDate, endDate, currentPage, searchType]);


    const value = {
        organizations,
        authors,
        selectedAuthors,
        searchWord,
        clips,
        onSearch,
        setSelectedAuthors,
        // --- 新增：将新状态和设置器传递给子组件 ---
        selectedFormat,
        setSelectedFormat,
        selectedContent,
        setSelectedContent,
        // --- 新增：时间范围筛选 ---
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        // --- 新增：分页 ---
        currentPage,
        totalPages,
        totalClips,
        onPageChange,
        // --- 新增：搜索类型 ---
        searchType,
        setSearchType
    };

    return (
        <context.Provider value={value}>
            <Container 
                maxWidth="xl" 
                sx={{ 
                    flex: 1,
                    display: 'flex',
                    py: { xs: 2, sm: 3, md: 4 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    width: '100%',
                    gap: { xs: 2, md: 4 },
                    flexDirection: { xs: 'column', md: 'row' },
                }}>
                    {/* Sidebar */}
                    <Box sx={{ 
                        width: { xs: '100%', md: '240px' },
                        flexShrink: 0,
                        position: { md: 'sticky' },
                        top: { md: '88px' },
                        alignSelf: { md: 'flex-start' },
                        maxHeight: { md: 'calc(100vh - 120px)' },
                    }}>
                        <Sidebar />
                    </Box>
                    
                    {/* Main Content */}
                    <Box sx={{ 
                        flex: 1,
                        minWidth: 0, // Prevents flex item from overflowing
                    }}>
                        <MainPanel />
                    </Box>
                </Box>
            </Container>
        </context.Provider>
    );
}